// app/(app)/inventory/stock/index.tsx
import { Button } from "@/components/default/forms";
import StockCard from "@/components/inventory/StockCard";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearError, fetchStockEntries } from "@/store/slices/stockSlice";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import DateTimePickerModal from "react-native-modal-datetime-picker";

export default function StockListScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { entries, pagination, loading, error } = useAppSelector(
    (state) => state.stock,
  );
  const [refreshing, setRefreshing] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isStartPickerVisible, setStartPickerVisible] = useState(false);
  const [isEndPickerVisible, setEndPickerVisible] = useState(false);
  const limit = 10;

  useEffect(() => {
    if (error) dispatch(clearError());
    dispatch(fetchStockEntries({ page: 1, limit, startDate, endDate }));
  }, [startDate, endDate]);

  const loadMore = () => {
    if (!loading && pagination.hasNextPage) {
      dispatch(
        fetchStockEntries({
          page: pagination.currentPage + 1,
          limit,
          startDate,
          endDate,
        }),
      );
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    dispatch(fetchStockEntries({ page: 1, limit, startDate, endDate })).finally(
      () => setRefreshing(false),
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      padding: spacing.lg,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    title: { fontSize: 24, fontWeight: "600", color: colors.text },
    filterRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.md,
      gap: spacing.sm,
    },
    filterButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.sm,
      alignItems: "center",
    },
    filterText: { fontSize: 14, color: colors.text },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
  });

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Stock Transactions</Text>
        <Button
          title="+ Add"
          onPress={() => router.push("/(app)/inventory/stock/add")}
          variant="primary"
          containerStyle={{ paddingHorizontal: 16, paddingVertical: 8 }}
        />
      </View>
      <View style={styles.filterRow}>
        <TouchableOpacity
          style={styles.filterButton}
          onPress={() => setStartPickerVisible(true)}
        >
          <Text style={styles.filterText}>
            {startDate
              ? new Date(startDate).toLocaleDateString()
              : "Start Date"}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.filterButton}
          onPress={() => setEndPickerVisible(true)}
        >
          <Text style={styles.filterText}>
            {endDate ? new Date(endDate).toLocaleDateString() : "End Date"}
          </Text>
        </TouchableOpacity>
        {(startDate || endDate) && (
          <TouchableOpacity
            style={[styles.filterButton, { borderColor: colors.danger }]}
            onPress={() => {
              setStartDate("");
              setEndDate("");
            }}
          >
            <Text style={{ color: colors.danger }}>Clear</Text>
          </TouchableOpacity>
        )}
      </View>
      <FlatList
        data={entries}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => (
          <StockCard entry={item} onRefresh={handleRefresh} />
        )}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        ListEmptyComponent={
          !loading ? (
            <Text style={styles.emptyText}>No transactions found</Text>
          ) : null
        }
        ListFooterComponent={
          loading ? (
            <ActivityIndicator
              size="small"
              color={colors.primary}
              style={{ marginVertical: spacing.lg }}
            />
          ) : null
        }
      />
      <DateTimePickerModal
        isVisible={isStartPickerVisible}
        mode="date"
        onConfirm={(date) => {
          setStartDate(date.toISOString());
          setStartPickerVisible(false);
        }}
        onCancel={() => setStartPickerVisible(false)}
      />
      <DateTimePickerModal
        isVisible={isEndPickerVisible}
        mode="date"
        onConfirm={(date) => {
          setEndDate(date.toISOString());
          setEndPickerVisible(false);
        }}
        onCancel={() => setEndPickerVisible(false)}
      />
    </View>
  );
}
